import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Post from '../components/Post';
import { normalizeImageUrl } from '../components/Post';
import TweetBox from '../components/TweetBox';
import SplashScreen from '../components/SplashScreen';
import Advertisement from '../components/Advertisement';
import advertisementService from '../utils/advertisementService';
import './Home.css';
import { apiService, clearCache, safeApiCall, clearDeletedPostCache, isPostIdDeleted, loadDeletedPostIds, saveDeletedPostId, removeFromDeletedPostIds } from '../utils/api';
import config from '../config';
import { CircularProgress, Button, Box, Container, Typography, Paper } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AreaSelector from '../components/AreaSelector';
import { useAuth } from '../contexts/AuthContext';
import PostSkeleton from '../components/PostSkeleton';
import axios from 'axios';
import { toast } from 'react-toastify';
import { setPageTitle, setMetaTags } from '../utils/seoUtils';

// 広告の掲載期間を計算する関数
const calculateAdPeriod = () => {
  // 日本時間で計算（UTC+9）
  const now = new Date();
  const japanTime = new Date(now.getTime() + (9 * 60 * 60 * 1000)); // UTC+9の日本時間
  
  const currentYear = japanTime.getFullYear();
  const currentMonth = japanTime.getMonth();
  const currentDay = japanTime.getDate();
  
  let startDate, endDate;
  
  // 常に21日～翌月20日の期間で判定
  startDate = new Date(currentYear, currentMonth, 21);
  endDate = new Date(currentYear, currentMonth + 1, 20, 23, 59, 59);
  
  return {
    startDate,
    endDate
  };
};

// 広告のステータスを取得する関数（期限チェック付き）
const getAdStatus = (ad) => {
  if (!ad) return 'invalid';
  
  const now = new Date();
  const { startDate, endDate } = calculateAdPeriod();
  
  // 広告の掲載期間と現在期間が重複しているかチェック
  if (ad.startDate && ad.endDate) {
    const adStartDate = new Date(ad.startDate);
    const adEndDate = new Date(ad.endDate);
    
    // 広告の掲載期間と現在期間が重複していない場合は期限切れ
    if (adEndDate < startDate || adStartDate > endDate) {
      return 'expired';
    }
  }
  
  // 基本的な期限チェック
  if (ad.startDate && ad.endDate) {
    const adStartDate = new Date(ad.startDate);
    const adEndDate = new Date(ad.endDate);
    
    if (adStartDate > now) {
      return 'reserved';
    }
    
    if (adEndDate < now) {
      return 'ended';
    }
  }
  
  return ad.status || 'active';
};

// 広告が有効かどうかをチェックする関数
const isAdValid = (ad) => {
  const status = getAdStatus(ad);
  // 有効なステータスは'active'のみ、期限切れや無効なものは除外
  return status === 'active';
};

function Home() {
  
  // シンプルなページネーションの状態管理
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const POSTS_PER_PAGE = 5; // 1ページあたり5件
  
  // 広告関連の状態
  const [topAds, setTopAds] = useState([]);
  const [feedAds, setFeedAds] = useState([]);
  const [isLoadingAds, setIsLoadingAds] = useState(false);
  
  // エリア関連の状態
  const [selectedArea, setSelectedArea] = useState({ prefecture: '', city: undefined });
  const [areaInitialized, setAreaInitialized] = useState(false);
  const [isAgeConfirmed, setIsAgeConfirmed] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false); // 重複読み込み防止フラグ
  
  // AreaSelector初期化制御フラグ
  const [areaSelectorInitialized, setAreaSelectorInitialized] = useState(false);
  
  // 新着投稿関連の状態
  const [hasNewPosts, setHasNewPosts] = useState(false);
  const [newPostsCount, setNewPostsCount] = useState(0);
  
  // その他の状態
  // オープニングロゴはセッション中に一度だけ表示する。
  // 別ページからホームに戻ってきたときなど、再マウントのたびに表示しないよう
  // sessionStorage のフラグで制御する（アプリ/タブを閉じて開き直すと再度表示）。
  const [showSplash, setShowSplash] = useState(() => {
    try {
      return sessionStorage.getItem('splashShown') !== '1';
    } catch (e) {
      return true;
    }
  });
  const [deletedPostIds, setDeletedPostIds] = useState(new Set());
  
  // 地域データを管理するstate
  const [areaData, setAreaData] = useState({
    prefectures: [],
    cities: []
  });

  // 地域コードから名前を取得するヘルパー関数
  const getPrefectureName = (code) => {
    if (!code || !areaData.prefectures.length) return 'なし';
    const prefecture = areaData.prefectures.find(p => p.code === code);
    return prefecture ? prefecture.name : 'なし';
  };

  const getCityName = (code) => {
    if (!code || !areaData.cities.length) return 'なし';
    const city = areaData.cities.find(c => c.code === code);
    return city ? city.name : 'なし';
  };
  
  // ユーザーアクション状態（一元管理）
  const [userActions, setUserActions] = useState({
    liked_posts: [],
    bookmarked_posts: [],
    retweeted_posts: [],
    loading: false,
    loaded: false
  });
  

  const navigate = useNavigate();
  const { currentUser, isLoggedIn, isInitialized, loading: authLoading } = useAuth();

  // ホームページのSEO設定
  useEffect(() => {
    setPageTitle('デリヘル専用SNS「DNS」 - 女の子との新しいコミュニケーションツール（風俗ポータル）', false);
    setMetaTags({
      title: 'デリヘル専用SNS「DNS」 - 女の子との新しいコミュニケーションツール（風俗ポータル）',
      description: 'デリヘル（風俗）専用SNS「DNS」（デリヘル・ネットワーク・サービス）では、働く女の子たちの投稿をチェックし、コメントやいいね、メッセージで気軽に交流できます。新しい出会いを楽しみましょう！当サイトは風俗店が掲載されています。18歳未満のご利用はご遠慮ください。',
      keywords: 'デリヘル, SNS, DNS, 風俗, コミュニケーション, 出会い, 投稿, いいね, メッセージ',
      type: 'website'
    });
  }, []);

  // 地域データを取得する関数
  const fetchAreaData = useCallback(async () => {
    try {
      // 都道府県データを取得
      const prefecturesResponse = await apiService.get('/api/areas/prefectures');
      const prefectures = prefecturesResponse.data || [];
      
      // 現在選択されている都道府県の市区町村データを取得
      let cities = [];
      if (selectedArea.prefecture) {
        const citiesResponse = await apiService.get(`/api/areas/cities/${selectedArea.prefecture}`);
        cities = citiesResponse.data || [];
      }
      
      setAreaData({ prefectures, cities });
      console.log('地域データ取得完了:', { prefectures: prefectures.length, cities: cities.length });
    } catch (error) {
      console.error('地域データ取得エラー:', error);
    }
  }, [selectedArea.prefecture]);
  
  // コンポーネントマウント時に削除済み投稿IDをクリア（バックエンドで削除済みの投稿はAPIから返されないため不要）
  useEffect(() => {
    try {
      localStorage.removeItem('deletedPosts');
      if (typeof window !== 'undefined') {
        window.deletedPostsCache = new Set();
      }
    } catch (e) {}
    setDeletedPostIds(new Set());
  }, []);

  // 地域データを初期化時に取得
  useEffect(() => {
    fetchAreaData();
  }, [fetchAreaData]);

  // コンポーネントマウント時にエリア設定を復元
  useEffect(() => {
    const checkAreaSettings = () => {
      try {
        const cachedSettings = localStorage.getItem('areaSettings');
        if (cachedSettings) {
          const settings = JSON.parse(cachedSettings);
          const now = new Date().getTime();
          const oneMonth = 30 * 24 * 60 * 60 * 1000; // 1ヶ月のミリ秒

          // 1ヶ月以内のキャッシュがある場合、selectedAreaにも反映
          if (settings.timestamp && (now - settings.timestamp < oneMonth)) {
            console.log('エリア設定のキャッシュが存在します - selectedAreaに復元します');
            
            // selectedAreaの状態も更新
            const areaState = {
              prefecture: settings.prefecture || '',
              city: settings.city !== undefined ? settings.city : undefined
            };
            setSelectedArea(areaState);
            console.log('Home: キャッシュからエリアを復元:', areaState);
            
            setAreaInitialized(true);
          } else {
            // 期限切れの場合はキャッシュをクリア
            localStorage.removeItem('areaSettings');
            console.log('エリア設定のキャッシュが期限切れのため削除しました');
            setAreaInitialized(true);
          }
        } else {
          console.log('エリア設定のキャッシュが見つかりません');
          setAreaInitialized(true);
        }
      } catch (err) {
        console.error('エリア設定の確認に失敗:', err);
        localStorage.removeItem('areaSettings'); // 破損したキャッシュを削除
        setAreaInitialized(true);
      }
    };

    checkAreaSettings();
  }, []);

  // ユーザーアクション取得関数（一元化）
  const fetchUserActions = useCallback(async () => {
    if (!isLoggedIn || userActions.loading || userActions.loaded) {
      return;
    }

    try {
      setUserActions(prev => ({ ...prev, loading: true }));
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('ユーザーアクション取得: トークンなし');
        return;
      }

      console.log('=== ユーザーアクション取得開始（一元化） ===');
      
      const response = await apiService.get('/api/posts/user-actions', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = response.data || { liked_posts: [], bookmarked_posts: [], retweeted_posts: [] };
      
      setUserActions({
        liked_posts: data.liked_posts || [],
        bookmarked_posts: data.bookmarked_posts || [],
        retweeted_posts: data.retweeted_posts || [],
        loading: false,
        loaded: true
      });
      
      console.log('ユーザーアクション取得完了:', {
        liked: data.liked_posts?.length || 0,
        bookmarked: data.bookmarked_posts?.length || 0,
        retweeted: data.retweeted_posts?.length || 0
      });
      
      // 詳細なデバッグ情報を出力
      console.log('ユーザーアクション詳細:', {
        liked_posts: data.liked_posts,
        bookmarked_posts: data.bookmarked_posts,
        retweeted_posts: data.retweeted_posts
      });
      
    } catch (error) {
      console.error('ユーザーアクション取得エラー:', error);
      setUserActions({
        liked_posts: [],
        bookmarked_posts: [],
        retweeted_posts: [],
        loading: false,
        loaded: false
      });
    }
  }, [isLoggedIn, userActions.loading, userActions.loaded]);

  // エリアトップ広告を取得する関数
  const fetchAreaTopAdvertisements = useCallback(async () => {
    try {
      setIsLoadingAds(true);
      console.log('=== エリアトップ広告取得開始 ===');
      console.log('選択エリア:', selectedArea);
      
      // エリアパラメータを構築
      const params = new URLSearchParams();
      if (selectedArea.prefecture) {
        params.append('prefecture', selectedArea.prefecture);
      }
      if (selectedArea.city) {
        params.append('city', selectedArea.city);
      }
      params.append('limit', '2'); // 最大2件まで
      
      const queryString = params.toString();
      const url = `/api/corporate/advertisements/top${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiService.get(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      const ads = response.data.advertisements || [];
      // 期限切れでない有効な広告のみをフィルタリング
      const validAds = ads.filter(ad => isAdValid(ad));
      console.log(`エリアトップ広告取得完了: ${ads.length}件（有効: ${validAds.length}件）`);
      setTopAds(validAds);
      
    } catch (error) {
      console.error('エリアトップ広告取得エラー:', error);
      console.error('エラー詳細:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      setTopAds([]);
    } finally {
      setIsLoadingAds(false);
    }
  }, [selectedArea]);

  // インフィード広告を取得する関数
  const fetchInfeedAdvertisements = useCallback(async () => {
    try {
      console.log('=== インフィード広告取得開始 ===');
      console.log('選択エリア:', selectedArea);
      
      // エリアパラメータを構築
      const params = new URLSearchParams();
      if (selectedArea.prefecture) {
        params.append('prefecture', selectedArea.prefecture);
      }
      if (selectedArea.city) {
        params.append('city', selectedArea.city);
      }
      params.append('count', '5'); // 5件別々の広告を取得
      params.append('sort_by_price', 'true'); // 単価順
      
      const queryString = params.toString();
      const url = `/api/corporate/advertisements/feed${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiService.get(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      const ads = response.data.advertisements || [];
      // 期限切れでない有効な広告のみをフィルタリング
      const validAds = ads.filter(ad => isAdValid(ad));
      console.log(`インフィード広告取得完了: ${ads.length}件（有効: ${validAds.length}件）`);
      setFeedAds(validAds);
      
    } catch (error) {
      console.error('インフィード広告取得エラー:', error);
      console.error('エラー詳細:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      setFeedAds([]);
    }
  }, [selectedArea]);

  // エリア変更のハンドラー
  const handleAreaChange = ({ prefecture, city, cities }) => {
    console.log('=== Home: handleAreaChange 呼び出し受信 ===');
    console.log('Home: エリア変更を受信:', { prefecture, city });
    console.log('Home: エリア変更の詳細:', { prefecture, city, cities, prefectureCode: prefecture?.code, cityCode: city?.code });
    
    // AreaSelectorが初期化中の場合は、初期化完了をマーク
    if (!areaSelectorInitialized) {
      console.log('Home: AreaSelector初期化完了をマーク');
      setAreaSelectorInitialized(true);
    }
    
    // 現在のisLoadingDataの状態をログ出力
    console.log('Home: 現在のisLoadingData:', isLoadingData);
    
    // 新しいエリア情報を構築
    const newArea = {
      prefecture: prefecture?.code || '',
      city: city?.code || (city === null ? '' : undefined), // nullの場合は空文字、undefinedの場合はundefined
      cities: cities?.map(c => c.code) || []
    };
    
    // "都道府県→なし"が選択された場合の特別処理
    if (prefecture === null || (prefecture && prefecture.code === '')) {
      console.log('Home: 都道府県"なし"が選択されました - 全国対象に設定');
      newArea.prefecture = '';
      newArea.city = ''; // 都道府県なしの場合は市区町村も空に
    }
    
    // 現在のエリアと比較して変更があるかチェック
    const hasChanged = 
      selectedArea.prefecture !== newArea.prefecture ||
      selectedArea.city !== newArea.city ||
      JSON.stringify(selectedArea.cities || []) !== JSON.stringify(newArea.cities || []);
    
    if (hasChanged) {
      console.log('Home: エリア変更実行:', {
        from: selectedArea,
        to: newArea
      });
      
      // 地域変更時のキャッシュクリア
      console.log('Home: 地域変更によるキャッシュクリア実行');
      
      // 投稿関連のキャッシュをクリア
      const cacheKeys = [
        '/api/posts',
        '/api/posts/user-actions',
        '/api/corporate/advertisements/top',
        '/api/corporate/advertisements/feed',
        '/api/stats/rankings'
      ];
      
      cacheKeys.forEach(key => {
        if (window.apiCache && window.apiCache[key]) {
          delete window.apiCache[key];
          console.log(`キャッシュクリア: ${key}`);
        }
      });
      
      // 投稿データをリセット
      setPosts([]);
      setCurrentPage(1);
      setHasMore(true);
      setLoading(false);
      setLoadingMore(false);
      
      // エリア情報を更新
      setSelectedArea(newArea);
      
      // エリア選択の詳細情報をログ出力
      console.log('Home: 選択エリア詳細:', {
        prefectureRaw: prefecture,
        cityRaw: city,
        prefectureCode: prefecture?.code,
        cityCode: city?.code
      });
      
      // エリア初期化完了をマーク
      if (!areaInitialized) {
        setAreaInitialized(true);
      }
      
      // エリア設定をlocalStorageに保存
      const areaSettings = {
        prefecture: prefecture?.code || '',
        cities: cities?.map(c => c.code) || [],
        city: city?.code || '',
        ageConfirmed: isAgeConfirmed,
        timestamp: new Date().getTime()
      };
      
      try {
        localStorage.setItem('areaSettings', JSON.stringify(areaSettings));
        console.log('Home: エリア設定をlocalStorageに保存:', areaSettings);
      } catch (err) {
        console.error('Home: エリア設定の保存に失敗:', err);
      }
    } else {
      console.log('Home: エリア変更なし - スキップ');
      
      // 初期化が完了していない場合のみ初期化完了をマーク
      if (!areaInitialized) {
        setAreaInitialized(true);
      }
      if (!areaSelectorInitialized) {
        setAreaSelectorInitialized(true);
      }
    }
    
    console.log('=== Home: handleAreaChange 処理完了 ===');
  };

  // シンプルな投稿データ処理関数
  const basicProcessPosts = (postsData, reset) => {
    if (reset) {
      setPosts(postsData);
    } else {
      setPosts(prevPosts => [...prevPosts, ...postsData]);
    }
    
    // hasMoreの判定 - 取得したデータがPOSTS_PER_PAGEより少ない場合は最後
    if (postsData.length < POSTS_PER_PAGE) {
      setHasMore(false);
    }
  };

  // 投稿の投稿者詳細情報をチェックする関数
  const checkPostAuthorDetails = useCallback(async (postId) => {
    try {
      const response = await apiService.get(`/api/posts/${postId}/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      return response.data;
    } catch (error) {
      
      return null;
    }
  }, []);

  // 投稿のバッチステータスを取得する関数
  const fetchPostsBatchStatus = async (postIds) => {
    if (!postIds || postIds.length === 0) return {};
    
    try {
      // セーフティガード - 最大10件までの投稿ステータスを取得
      const limitedPostIds = postIds.slice(0, 10);
      
      // 個別の投稿ステータスを並列で取得
      const statusPromises = limitedPostIds.map(postId => 
        apiService.get(`/api/posts/${postId}/status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        }).catch(err => {
          console.error(`ステータス取得エラー:`, err.message);
          return { data: {} }; // エラー時は空オブジェクトを返す
        })
      );
      
      // すべてのリクエストを並列に実行
      const responses = await Promise.all(statusPromises);
      
      // レスポンスを投稿IDをキーとするオブジェクトに変換
      const result = {};
      responses.forEach((response, index) => {
        const postId = limitedPostIds[index];
        const statusData = response.data || {};
        
        // ブロック関連の情報を確実に含める
        result[postId] = {
          liked: statusData.liked || statusData.is_liked || false,
          bookmarked: statusData.bookmarked || statusData.is_bookmarked || false,
          retweeted: statusData.retweeted || statusData.is_retweeted || false,
          // ブロック関連の情報
          is_blocked: statusData.is_blocked || false,         // 自分がブロックしているか
          blocked_by_user: statusData.blocked_by_user || false, // 相手にブロックされているか
          // 他の追加情報もあれば保持
          ...statusData
        };
      });
      
      return result || {};
    } catch (error) {
      
      return {};
    }
  };

  // 投稿データを処理する関数（バッチ処理を実装）
  const processPostsData = useCallback(async (postsData, reset) => {
    if (!postsData || postsData.length === 0) {
      if (reset) setPosts([]);
      return;
    }
    
    try {
      // 削除済み投稿IDをフィルタリング（セッション中に削除した投稿のみ）
      let filteredPosts = postsData.filter(post => !post.is_deleted);
      
      // 簡略化された処理
      if (reset) {
        setPosts(filteredPosts);
      } else {
        setPosts(prevPosts => [...prevPosts, ...filteredPosts]);
      }
    } catch (error) {
      
      if (reset) setPosts([]);
    }
  }, []);

  // 投稿のいいね処理（Post.jsに移行）
  const handleLikePost = (postId) => {
    // Post.jsのhandleLike関数がすべて処理するため、ここではuserActionsの同期のみ
    const isCurrentlyLiked = userActions.liked_posts.some(id => String(id) === String(postId));
    
    // userActions状態を即座に更新（楽観的更新）
    setUserActions(prevActions => ({
      ...prevActions,
      liked_posts: isCurrentlyLiked 
        ? prevActions.liked_posts.filter(id => String(id) !== String(postId))
        : [...prevActions.liked_posts, postId]
    }));

    // postsのカウントも即座に更新
    setPosts(prevPosts => 
      prevPosts.map(post => {
        if (String(post.id) === String(postId)) {
          return {
            ...post,
            likes_count: isCurrentlyLiked 
              ? Math.max(0, (post.likes_count || 0) - 1)
              : (post.likes_count || 0) + 1
          };
        }
        return post;
      })
    );
  };

  // 投稿のリツイート処理
  const handleRetweetPost = async (postId) => {
    try {
      // 現在のリツイート状態を確認
      const isCurrentlyRetweeted = userActions.retweeted_posts.some(id => String(id) === String(postId));
      
      if (isCurrentlyRetweeted) {
        // 既にリツイート済みなら解除
        // 楽観的UI更新
        setPosts(prevPosts => 
          prevPosts.map(post => {
            if (post.id === postId) {
              return {
                ...post,
                retweeted: false,
                reposts_count: Math.max(0, (post.reposts_count || 1) - 1)
              };
            }
            return post;
          })
        );
        
        // userActions も同期更新
        setUserActions(prevActions => ({
          ...prevActions,
          retweeted_posts: prevActions.retweeted_posts.filter(id => String(id) !== String(postId))
        }));
        
        // APIコール（リツイート解除）
        await apiService.delete(`/api/posts/${postId}/retweet`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });
      } else {
        // リツイートする
        // 楽観的UI更新
        setPosts(prevPosts => 
          prevPosts.map(post => {
            if (post.id === postId) {
              return {
                ...post,
                retweeted: true,
                reposts_count: (post.reposts_count || 0) + 1
              };
            }
            return post;
          })
        );
        
        // userActions も同期更新
        setUserActions(prevActions => ({
          ...prevActions,
          retweeted_posts: [...prevActions.retweeted_posts, postId]
        }));
        
        // APIコール（リツイート追加）
        await apiService.post(`/api/posts/${postId}/retweet`, {}, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });
      }
    } catch (error) {
      console.error('リツイート処理エラー:', error);
      
      // エラー時に元の状態に戻す（楽観的UI更新をロールバック）
      const isCurrentlyRetweeted = userActions.retweeted_posts.some(id => String(id) === String(postId));
      
      setPosts(prevPosts => 
        prevPosts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              retweeted: isCurrentlyRetweeted,
              reposts_count: isCurrentlyRetweeted ? 
                (post.reposts_count || 0) + 1 : 
                Math.max(0, (post.reposts_count || 1) - 1)
            };
          }
          return post;
        })
      );
      
      // userActions もロールバック
      setUserActions(prevActions => ({
        ...prevActions,
        retweeted_posts: isCurrentlyRetweeted ? 
          [...prevActions.retweeted_posts, postId] :
          prevActions.retweeted_posts.filter(id => String(id) !== String(postId))
      }));
      
      toast.error('リツイートの処理中にエラーが発生しました');
    }
  };

  // 投稿へのコメント処理
  const handleCommentPost = (postId) => {
    // 投稿オブジェクトを見つける
    const post = posts.find(p => p.id === postId);
    if (post) {
      // コメント数を更新
      post.comments_count = (post.comments_count || 0) + 1;
      // 投稿配列を更新
      setPosts([...posts]);
    }
    // 詳細ページへの遷移はしない
  };

  // 投稿の削除処理
  const handleDeletePost = async (postId) => {
    try {
      // 削除の確認
      if (!window.confirm('この投稿を削除してもよろしいですか？')) {
        return;
      }
      
      // 削除した投稿IDをリストに追加
      saveDeletedPostId(postId);
      
      // 楽観的UI更新 - is_deletedフラグをセット（Profile.jsと統一）
      setPosts(prevPosts => prevPosts.map(post => 
        String(post.id) === String(postId) ? { ...post, is_deleted: true } : post
      ));
      
      try {
        // APIコール
        await safeApiCall.delete(`/api/posts/${postId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });
        
        toast.success('投稿が削除されました');
      } catch (error) {
        // エラー発生時の処理
        
        
        // エラーの種類に応じたメッセージを表示
        if (error.response) {
          // サーバーからのレスポンスがある場合
          if (error.response.status === 404) {
            // 404エラー - 投稿が既に削除されている
            toast.info('投稿はすでに削除されています');
          } else {
            // その他のHTTPエラー
            toast.warn('サーバーからエラーレスポンスを受信しました。投稿は削除されました。');
          }
        } else if (error.request) {
          // リクエストは送信されたがレスポンスがない場合（ネットワークエラーなど）
          toast.warn('ネットワークエラーが発生しましたが、投稿は削除されました。');
        } else {
          // リクエスト設定中のエラー
          toast.warn('エラーが発生しましたが、投稿は削除されました。');
        }
      }
    } catch (error) {
      
      toast.error('投稿の削除中にエラーが発生しました');
    }
  };

  // 投稿のブックマーク追加処理
  const handleSavePost = async (postId) => {
    try {
      // ログインチェック
      if (!isLoggedIn || !currentUser) {
        toast.error('ブックマークするにはログインが必要です');
        navigate('/login');
        return;
      }
      
      // 投稿の状態を更新（楽観的UI更新）
      setPosts(prevPosts => 
        prevPosts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              bookmarked: true,
              is_bookmarked: true
            };
          }
          return post;
        })
      );
      
      // userActions も同期更新
      setUserActions(prevActions => ({
        ...prevActions,
        bookmarked_posts: [...prevActions.bookmarked_posts, postId]
      }));
      
      // APIコール
      const response = await apiService.post(`/api/posts/${postId}/bookmark`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
    } catch (error) {
      console.error('ブックマーク追加エラー:', error);
      
      // エラー時に元の状態に戻す
      setPosts(prevPosts => 
        prevPosts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              bookmarked: false,
              is_bookmarked: false
            };
          }
          return post;
        })
      );
      
      // userActions もロールバック
      setUserActions(prevActions => ({
        ...prevActions,
        bookmarked_posts: prevActions.bookmarked_posts.filter(id => String(id) !== String(postId))
      }));
      
      toast.error('ブックマークの追加中にエラーが発生しました');
    }
  };

  // 投稿のブックマーク削除処理
  const handleRemoveSavedPost = async (postId) => {
    try {
      // ログインチェック
      if (!isLoggedIn || !currentUser) {
        toast.error('ブックマークを削除するにはログインが必要です');
        return;
      }
      
      // 投稿の状態を更新（楽観的UI更新）
      setPosts(prevPosts => 
        prevPosts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              bookmarked: false,
              is_bookmarked: false
            };
          }
          return post;
        })
      );
      
      // userActions も同期更新
      setUserActions(prevActions => ({
        ...prevActions,
        bookmarked_posts: prevActions.bookmarked_posts.filter(id => String(id) !== String(postId))
      }));
      
      // APIコール
      const response = await apiService.delete(`/api/posts/${postId}/bookmark`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
    } catch (error) {
      console.error('ブックマーク削除エラー:', error);
      
      // エラー時に元の状態に戻す
      setPosts(prevPosts => 
        prevPosts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              bookmarked: true,
              is_bookmarked: true
            };
          }
          return post;
        })
      );
      
      // userActions もロールバック
      setUserActions(prevActions => ({
        ...prevActions,
        bookmarked_posts: [...prevActions.bookmarked_posts, postId]
      }));
      
      toast.error('ブックマークの削除中にエラーが発生しました');
    }
  };



  // エリアの投稿IDリストを取得する関数
  // シンプルなページネーションベースの投稿取得関数
  const loadPosts = useCallback(async (page = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setError(null);
        setPosts([]); // 既存の投稿をクリア
        setHasMore(true); // hasMoreをリセット
      } else {
        setLoadingMore(true);
      }

      console.log(`=== 投稿取得開始: ページ${page} (${POSTS_PER_PAGE}件/ページ) ===`);
      console.log(`処理モード: ${reset ? 'リセット（初回読み込み）' : '追加読み込み'}`);
      console.log(`現在の投稿数: ${posts.length}件`);
      
      const params = {
        page: page,
        limit: POSTS_PER_PAGE,
        exclude_advertisements: true // 通常投稿のみ（広告除外）
      };
      
      // エリアフィルターを適用
      if (selectedArea.prefecture && selectedArea.prefecture !== 'all' && selectedArea.prefecture !== '') {
        params.prefecture = selectedArea.prefecture;
        console.log('都道府県フィルター適用:', selectedArea.prefecture);
        
        if (selectedArea.city && selectedArea.city !== 'all' && selectedArea.city !== '') {
          params.cities = [selectedArea.city];
          console.log('市区町村フィルター適用:', selectedArea.city);
        }
      } else {
        console.log('エリアフィルター未適用: 全エリアの投稿を取得');
      }
      
      console.log('=== 投稿取得パラメータ詳細 ===');
      console.log('パラメータ:', JSON.stringify(params, null, 2));
      console.log('現在の選択エリア状態:', JSON.stringify(selectedArea, null, 2));
      console.log('exclude_advertisements:', params.exclude_advertisements);
      console.log('prefecture:', params.prefecture);
      console.log('cities:', params.cities);
      console.log('リクエストURL:', `/api/posts?${new URLSearchParams(params).toString()}`);
      
      const response = await apiService.get('/api/posts', {
        params,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      console.log('=== APIレスポンス詳細 ===');
      console.log('レスポンスステータス:', response.status);
      console.log('レスポンスヘッダー:', response.headers);
      console.log('レスポンスデータ構造:', {
        hasData: !!response.data,
        hasPosts: !!response.data?.posts,
        postsCount: response.data?.posts?.length || 0,
        otherKeys: Object.keys(response.data || {})
      });
      
      // 広告除外デバッグ情報をコンソールに表示
      if (response.data.ad_exclusion_debug) {
        console.log('🚨 === 広告除外処理デバッグ情報 ===');
        const debug = response.data.ad_exclusion_debug;
        
        if (debug.error) {
          console.error('❌ 広告除外処理エラー:', debug.error_message);
          console.error('エラータイプ:', debug.error_type);
          console.error('発生時刻:', debug.timestamp);
        } else {
          console.log('✅ 広告除外処理成功');
          console.log('処理開始時刻:', debug.timestamp);
          console.log('除外対象の広告投稿ID:', debug.excluded_ad_post_ids);
          console.log('除外投稿数:', debug.excluded_count);
          console.log('クエリ適用状況:', debug.query_applied ? '適用済み' : '未適用');
          console.log('処理メッセージ:', debug.message);
          
          if (debug.suspicious_ids_check) {
            console.log('疑わしい投稿IDチェック結果:');
            Object.entries(debug.suspicious_ids_check).forEach(([id, exists]) => {
              console.log(`  投稿ID ${id}: AdPostテーブルに${exists ? '存在' : '存在しない'}`);
            });
          }
        }
        console.log('=== 広告除外デバッグ情報終了 ===');
      }
      
      let postsData = response.data.posts || response.data || [];
      if (!Array.isArray(postsData)) {
        postsData = [];
      }
      
      // 削除済み投稿をフィルタリング（セッション中に削除した投稿のみ）
      postsData = postsData.filter(post => !post.is_deleted);
      
      console.log(`投稿取得完了: ${postsData.length}件（削除済み除外後）`);

      // 閲覧記録を非同期で送信
      if (postsData.length > 0) {
        const postIds = postsData.map(p => p.id).filter(Boolean);
        try {
          apiService.post('/api/posts/record-views', { post_ids: postIds }).catch(() => {});
        } catch (e) {}
      }
      console.log(`hasMore現在状態: ${hasMore}`);
      console.log(`現在のページ: ${currentPage}`);
      
      // 取得した投稿のエリア情報をデバッグ出力
      if (postsData.length > 0) {
        console.log('取得投稿のエリア詳細:');
        postsData.slice(0, 5).forEach((post, index) => {
          const prefectureName = getPrefectureName(post.prefecture_code);
          const cityName = getCityName(post.city_code);
          console.log(`  投稿${index + 1}: ID=${post.id}, 都道府県=${prefectureName}(${post.prefecture_code || 'なし'}), 市区町村=${cityName}(${post.city_code || 'なし'}), 投稿者=${post.author?.username || 'unknown'}, 内容="${post.content?.substring(0, 30) || 'なし'}..."`);
        });
        
        // 広告投稿の可能性がある投稿を特別にチェック
        const suspiciousAdPosts = postsData.filter(post => 
          post.content && (
            post.content.includes('広告') || 
            post.content.includes('宣伝') || 
            post.content.includes('クリック') ||
            post.author?.username?.includes('corporate') ||
            post.author?.username?.includes('ad')
          )
        );
        
        // AdPostテーブルに登録されている投稿IDかチェック
        const potentialAdPostIds = postsData.filter(post => 
          post.is_advertisement === true || 
          post.ad_post_id || 
          post.advertisement_id ||
          (post.author && (
            post.author.role === 'corporate' || 
            post.author.role === 'advertiser'
          ))
        );
        
        if (suspiciousAdPosts.length > 0) {
          console.warn('⚠️ 広告の可能性がある投稿が検出されました:');
          suspiciousAdPosts.forEach((post, index) => {
            console.warn(`  疑わしい投稿${index + 1}: ID=${post.id}, 投稿者=${post.author?.username}, ロール=${post.author?.role}, 内容="${post.content?.substring(0, 50)}..."`);
          });
        }
        
        if (potentialAdPostIds.length > 0) {
          console.error('🚨 広告として登録されている投稿が通常投稿に混入しています:');
          potentialAdPostIds.forEach((post, index) => {
            console.error(`  広告投稿${index + 1}: ID=${post.id}, 投稿者=${post.author?.username}, ロール=${post.author?.role}, is_advertisement=${post.is_advertisement}, ad_post_id=${post.ad_post_id}`);
          });
        }
      } else {
        console.log('取得した投稿が0件です');
      }
      
      if (reset) {
        setPosts(postsData);
        console.log('投稿リセット完了');
      } else {
        // 重複チェック付きで投稿を追加
        setPosts(prevPosts => {
          const existingIds = new Set(prevPosts.map(post => post.id));
          const newPosts = postsData.filter(post => !existingIds.has(post.id));
          
          console.log(`重複チェック詳細:`);
          console.log(`既存投稿IDs: [${Array.from(existingIds).join(', ')}]`);
          console.log(`取得投稿IDs: [${postsData.map(p => p.id).join(', ')}]`);
          console.log(`新規投稿IDs: [${newPosts.map(p => p.id).join(', ')}]`);
          console.log(`重複チェック: 既存${prevPosts.length}件、新規${newPosts.length}件、重複除外${postsData.length - newPosts.length}件`);
          
          // 新規投稿のエリア情報をデバッグ出力
          if (newPosts.length > 0) {
            console.log('新規投稿のエリア詳細:');
            newPosts.slice(0, 3).forEach((post, index) => {
              const prefectureName = getPrefectureName(post.prefecture_code);
              const cityName = getCityName(post.city_code);
              console.log(`  新規投稿${index + 1}: ID=${post.id}, 都道府県=${prefectureName}(${post.prefecture_code || 'なし'}), 市区町村=${cityName}(${post.city_code || 'なし'}), 投稿者=${post.author?.username || 'unknown'}`);
            });
          }
          
          return [...prevPosts, ...newPosts];
        });
        console.log('投稿追加完了（重複除外済み）');
      }
      
      // 次のページがあるかチェック
      if (reset) {
        // リセット時は取得した投稿数でhasMoreを判定
        setHasMore(postsData.length === POSTS_PER_PAGE);
      } else {
        // 追加読み込み時は、API応答の投稿数が期待値未満の場合のみhasMoreをfalse
        // 新規投稿が0件でも、実際にはまだ投稿がある可能性があるため、
        // APIから返された投稿数が少ない場合のみhasMoreをfalseにする
        if (postsData.length < POSTS_PER_PAGE) {
          console.log(`API応答の投稿数が期待値未満のため、hasMoreをfalseに設定: 取得数=${postsData.length}, 期待値=${POSTS_PER_PAGE}`);
          setHasMore(false);
        } else {
          console.log(`API応答の投稿数が期待値通りのため、hasMoreを維持: 取得数=${postsData.length}, 期待値=${POSTS_PER_PAGE}`);
        }
      }
      
      // ページ番号を更新
      setCurrentPage(page);
      
    } catch (error) {
      console.error('投稿取得エラー:', error);
      setError('投稿の読み込みに失敗しました。');
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedArea, POSTS_PER_PAGE]);

  // 次のページを読み込む関数
  const loadMorePosts = useCallback(() => {
    console.log(`=== loadMorePosts呼び出し ===`);
    console.log(`currentPage: ${currentPage}`);
    console.log(`loadingMore: ${loadingMore}`);
    console.log(`hasMore: ${hasMore}`);
    console.log(`posts.length: ${posts.length}`);
    
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      console.log(`次のページ読み込み開始: ${nextPage}`);
      loadPosts(nextPage, false);
    } else {
      console.log(`読み込みスキップ理由: loadingMore=${loadingMore}, hasMore=${hasMore}`);
    }
  }, [currentPage, loadingMore, hasMore, loadPosts, posts.length]);

  // 新しい投稿をロードする関数
  const loadNewPosts = useCallback(() => {
    if (hasNewPosts) {
      setHasNewPosts(false);
      setNewPostsCount(0);
      loadPosts(1, true); // 1ページ目をリセットして読み込み
    }
  }, [hasNewPosts]);

  // 初期投稿読み込み＆エリア変更対応のuseEffect（市区町村選択状況判定後実行）
  useEffect(() => {
    // エリアが初期化されており、認証も完了しており、AreaSelectorも初期化完了しており、かつ重複読み込み中でない場合のみ実行
    // 市区町村の選択状況を判定してから実行
    const shouldLoadPosts = areaInitialized && areaSelectorInitialized && isInitialized && !authLoading && !isLoadingData && (
      // 都道府県が未選択（空文字''）の場合は即座に実行（全エリア対象）
      selectedArea.prefecture === '' || 
      // 都道府県が選択されている場合は、市区町村の選択状況が判定済みの場合に実行
      // city !== undefined は「市区町村選択が判定済み」を意味する（空文字''も含む）
      (selectedArea.prefecture && selectedArea.city !== undefined)
    );
    
    if (shouldLoadPosts) {
      console.log('=== 投稿・広告読み込み実行 ===');
      console.log('環境:', process.env.NODE_ENV);
      console.log('areaInitialized:', areaInitialized);
      console.log('areaSelectorInitialized:', areaSelectorInitialized);
      console.log('isInitialized:', isInitialized);
      console.log('selectedArea:', selectedArea);
      console.log('isLoadingData:', isLoadingData);
      
      // エリア選択状況をログ出力
      if (selectedArea.prefecture === '') {
        console.log('エリア選択状況: 全エリア対象（都道府県なし）');
      } else if (selectedArea.city === '') {
        console.log('エリア選択状況: 都道府県のみ選択 -', selectedArea.prefecture);
      } else {
        console.log('エリア選択状況: 都道府県+市区町村選択 -', selectedArea.prefecture, selectedArea.city);
      }
      
      // 重複読み込み防止フラグを設定
      setIsLoadingData(true);
      
      // ユーザーアクションのロード状態をリセット（データは保持）
      setUserActions(prev => ({
        ...prev,
        loading: true,
        loaded: false
      }));
      fetchUserActions();
      
      // 投稿データを取得（1ページ目）
      loadPosts(1, true);
      
      // 広告読み込み
      setTimeout(() => {
        fetchAreaTopAdvertisements();
        fetchInfeedAdvertisements();
        
        // 読み込み完了後にフラグをリセット（1秒後）
        setTimeout(() => {
          setIsLoadingData(false);
        }, 1000);
      }, 500);
    }
  }, [areaInitialized, areaSelectorInitialized, isInitialized, authLoading, selectedArea]); // areaSelectorInitializedを追加

  // 無限スクロール機能（60%でトリガー）
  useEffect(() => {
    let timeoutId = null;

    const handleScroll = () => {
      // 既存のタイマーをクリア（スロットリング）
      if (timeoutId) clearTimeout(timeoutId);
      
      timeoutId = setTimeout(() => {
        // ローディング中、さらに読み込む投稿がない、またはすでに読み込み中の場合はスキップ
        if (loading || loadingMore || !hasMore) {
          return;
        }

        // ページの高さとスクロール位置を取得
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        // スクロール位置が60%に達したかチェック
        const scrollPercentage = (scrollTop + windowHeight) / documentHeight;
        
        if (scrollPercentage >= 0.6) {
          console.log(`スクロール60%に到達 - 次の5件を読み込み (現在ページ: ${currentPage}, hasMore: ${hasMore}, loadingMore: ${loadingMore})`);
          loadMorePosts(); // 次のページを読み込み
        }
      }, 100); // 100msのスロットリング
    };

    // スクロールイベントリスナーを追加
    window.addEventListener('scroll', handleScroll);
    
    // クリーンアップ関数
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loading, loadingMore, hasMore, loadMorePosts]);

  // 年齢確認のハンドラー
  const handleAgeConfirm = (confirmed) => {
    console.log('Home: 年齢確認状態変更:', confirmed);
    setIsAgeConfirmed(confirmed);
  };

  // 新しい投稿を追加する関数
  const addPost = (newPost) => {
    setPosts(prevPosts => [newPost, ...prevPosts]);
    setNewPostsCount(prevCount => prevCount + 1);
    setHasNewPosts(true);
  };

  // 未認証ユーザー向けの登録促進コンポーネント
  const GuestPrompt = () => (
    <Paper className="home__guestPrompt">
      <Typography variant="h5" gutterBottom>
        あなただけのお気に入りを見つけよう！
      </Typography>
      <Typography variant="body1" paragraph>
        アカウント登録してSNSに参加する。
      </Typography>
      <div className="home__guestButtons">
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={() => navigate('/login')}
          className="home__guestLoginButton"
        >
          ログイン
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/register')}
          className="home__guestRegisterButton"
        >
          新規登録
        </Button>
      </div>
    </Paper>
  );

  // パフォーマンス最適化: 投稿リストのメモ化
  const memoizedPosts = useMemo(() => {
    console.log('=== memoizedPosts 処理開始 ===');
    console.log('posts:', posts);
    console.log('userActions:', userActions);
    
    if (!Array.isArray(posts)) {
      console.log('posts is not array:', posts);
      return [];
    }
        
    const processedPosts = posts.filter(post => !post.is_deleted).map((post, index) => {
      try {
        const postId = post.id || Math.random().toString();
        const isRetweet = post.is_retweet || post.isRetweet || false;
        const retweeterId = post.retweeter?.id || 'unknown';
        
        const originalAuthor = isRetweet && (post.original_author || {});
        const author = post.author || post.user || {};
        
        // ユーザーアクションからこの投稿の状態を判定（数値で統一して判定）
        // リツイートの場合は original_post_id で判定する
        const actionTargetId = (isRetweet && post.original_post_id) ? post.original_post_id : post.id;
        const isLiked = userActions.liked_posts.some(id => String(id) === String(actionTargetId));
        const isBookmarked = userActions.bookmarked_posts.some(id => String(id) === String(actionTargetId));
        const isRetweeted = userActions.retweeted_posts.some(id => String(id) === String(actionTargetId));
        
        // デバッグ: ユーザーアクションの判定結果をログ出力（簡略版）
        if (index < 2) { // 最初の2件のみ簡単ログ
          console.log(`投稿${post.id}: アクション状態`, {
            liked: isLiked,
            bookmarked: isBookmarked,
            retweeted: isRetweeted
          });
        }
        
        // Postコンポーネントが期待する形式でpropsを渡す
        return (
          <Post
            key={`post-${postId}-${isRetweet ? retweeterId : 'original'}`}
            id={post.original_post_id || post.id}
            text={post.content || post.text || ''}
            displayName={author.display_name || author.displayName || author.username || 'ユーザー'}
            username={author.username || 'unknown'}
            verified={author.verified || false}
            avatar={author.avatar || ''}
            image={post.image || post.image_url}
            video={post.video || post.video_url}
            createdAt={post.created_at}
            likes={post.likes_count || post.like_count || 0}
            comments={post.comments_count || post.comment_count || 0}
            retweets={post.reposts_count || post.retweet_count || 0}
            isRetweet={isRetweet}
            retweeter={post.retweeter}
            retweeted={isRetweeted}
            authorId={author.id || post.user_id}
            isPinned={post.is_pinned || false}
            postLocation={post.location}
            shifts={post.shifts}
            has_poll={post.has_poll || false}
            poll={post.poll}
            has_voted={post.has_voted || false}
            liked={isLiked}
            bookmarked={isBookmarked} // 一元管理されたユーザーアクションから取得
            userActionsLoaded={userActions.loaded} // Post.jsで重複取得を防ぐフラグ
            onLike={handleLikePost}
            onRetweet={handleRetweetPost}
            onComment={handleCommentPost}
            onDelete={handleDeletePost}
            onSave={handleSavePost}
            onRemoveSave={handleRemoveSavedPost}
          />
        );
      } catch (error) {
        console.error('投稿レンダリングエラー:', error);
        return null;
      }
    }).filter(post => post !== null);
    
    console.log('=== memoizedPosts 処理完了 ===');
    console.log('生成された投稿数:', processedPosts.length);
    return processedPosts;
  }, [posts, currentUser, userActions, handleLikePost, handleRetweetPost, handleCommentPost, handleDeletePost, handleSavePost, handleRemoveSavedPost]);

  // スプラッシュスクリーン表示のロジック
  const handleCloseSplash = () => {
    setShowSplash(false);
    // 表示済みフラグを立て、以降このセッションでは再表示しない
    try {
      sessionStorage.setItem('splashShown', '1');
    } catch (e) {}
  };

  return (
    <Container maxWidth="sm">
      {showSplash && <SplashScreen onClose={handleCloseSplash} />}
      
      <Box sx={{ my: 2 }}>
        <AreaSelector
          onAreaChange={handleAreaChange}
          onAgeConfirm={handleAgeConfirm}
          singleCitySelect={true}
          isGuestUser={!isLoggedIn}
        />
      </Box>

      <div className="home">
        <div className="home__header">
          <h2>ホーム</h2>
        </div>

        {/* TweetBoxの代わりに条件付きレンダリング */}
        {isLoggedIn ? (
          <TweetBox addPost={addPost} />
        ) : (
          <GuestPrompt />
        )}
        
        {loading && (
          <div className="home__loading">
            <CircularProgress size={32} />
            <p>読み込み中...</p>
          </div>
        )}
        
        {!loading && error && (
          <div className="home__error">
            <p>{error}</p>
            <Button variant="outlined" onClick={() => loadPosts(1, true)}>再試行</Button>
          </div>
        )}
        
        {!loading && !error && (
          <div className="home__feed">

            
            {/* エリアトップ広告 */}
            {topAds.filter(ad => isAdValid(ad)).length > 0 && (
              <div className="home__areaTopAds">
                {topAds.filter(ad => isAdValid(ad)).map((ad, index) => {
                  // タイトルと説明文を処理
                  let title = ad.title || '';
                  let description = ad.description || '';
                  
                  // APIから受け取ったタイトルが\n\n区切りの場合、分離する
                  if (title && title.includes('\n\n')) {
                    const parts = title.split('\n\n');
                    title = parts[0];
                    // タイトルから分離した部分を説明文として使用
                    description = parts.slice(1).join('\n\n');
                  }
                  
                  return (
                    <Advertisement
                      key={`area-top-${ad.id || index}`}
                      id={ad.id}
                      title={title}
                      description={description}
                      image={ad.image}
                      url={ad.url}
                      companyName={ad.company_name || ad.companyName}
                      type="area-top"
                      position={index}
                    />
                  );
                })}
              </div>
            )}
            
            {/* 新しい投稿があることを示すバナー（エリアトップ広告のすぐ下） */}
            {hasNewPosts && (
              <div className="home__newPostsBanner" onClick={loadNewPosts}>
                <RefreshIcon />
                <span>{newPostsCount}件の新しい投稿があります</span>
              </div>
            )}
            
            {/* 投稿がない場合のメッセージ */}
            {memoizedPosts.length === 0 && (
              <>
                {/* 掲載店募集中の表示（広告と同じフォーマット） */}
                <Advertisement
                  id="recruitment"
                  title="掲載店募集中"
                  description="このエリアでお店やサービスを宣伝しませんか？お客様のデリヘルを地域の皆様にアピールできます。"
                  image="images/default-ad-recruitment.gif"
                  url="https://deli-ns.com/ads"
                  companyName="deli-ns.com"
                  type="recruitment"
                />
                
                <Paper className="home__noPosts" sx={{ p: 3, my: 2, textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    表示できる投稿がありません
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    このエリアにはまだ投稿がありません。
                  </Typography>
                  <Button 
                    variant="outlined" 
                    startIcon={<RefreshIcon />} 
                    onClick={() => loadPosts(1, true)}
                    sx={{ mt: 2 }}
                    disabled={loading}
                  >
                    {loading ? '更新中...' : '再読み込み'}
                  </Button>
                </Paper>
              </>
            )}
            
            {/* 投稿とインフィード広告の表示 */}
            {memoizedPosts.map((post, index) => {
              const elements = [post];
              
              // 5件ごと（インデックス4, 9, 14, 19...の後）にインフィード広告を挿入
              // 単価の高い順で5件別々の広告を循環表示
              const validFeedAds = feedAds.filter(ad => isAdValid(ad));
              if ((index + 1) % 5 === 0 && validFeedAds.length > 0) {
                const adPosition = Math.floor(index / 5); // 何回目の広告挿入か
                // 広告数が足りない場合は循環表示（例：3件の場合 1→2→3→1→2→3...）
                const adIndex = adPosition % validFeedAds.length;
                const feedAd = validFeedAds[adIndex];
                
                console.log(`インフィード広告表示: 位置${adPosition}, 広告インデックス${adIndex}/${validFeedAds.length-1}, 広告ID: ${feedAd.id}`);
                
                // タイトルと説明文を処理
                let title = feedAd.title || '';
                let description = feedAd.description || '';
                
                // APIから受け取ったタイトルが\n\n区切りの場合、分離する
                if (title && title.includes('\n\n')) {
                  const parts = title.split('\n\n');
                  title = parts[0];
                  // タイトルから分離した部分を説明文として使用
                  description = parts.slice(1).join('\n\n');
                }
                
                elements.push(
                  <Advertisement
                    key={`infeed-${feedAd.id || 0}-pos${adPosition}-idx${adIndex}`}
                    id={feedAd.id}
                    title={title}
                    description={description}
                    image={feedAd.image}
                    url={feedAd.url}
                    companyName={feedAd.company_name || feedAd.companyName}
                    type="infeed"
                    position={adPosition}
                  />
                );
              }
              
              return elements;
            }).flat()}
            
            {/* 読み込み中の表示 */}
            {loadingMore && (
              <div className="home__loadingMore">
                <CircularProgress size={24} />
                <p>さらに読み込み中... (ページ{currentPage})</p>
              </div>
            )}
            
            {/* これ以上投稿がない場合の表示 */}
            {!loading && !loadingMore && !hasMore && memoizedPosts.length > 0 && (
              <Paper sx={{ p: 2, my: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                <Typography variant="body2" color="textSecondary">
                  これ以上投稿はありません
                </Typography>
              </Paper>
            )}
          </div>
        )}
      </div>
    </Container>
  );
}

export default Home;
